import styled from 'styled-components';
import type { CalculatedOrder, CalculatedOrderLineItem } from '../../lib/pricing/calcOrder';
import { formatUsdCents } from '../../lib/pricing/formatMoney';

const Wrap = styled.section<{ $flat?: boolean }>`
  border: ${({ $flat, theme }) => ($flat ? 'none' : `1px solid ${theme.colors.gray200}`)};
  border-radius: ${({ $flat }) => ($flat ? '0' : '14px')};
  padding: ${({ $flat }) => ($flat ? '0' : '1rem')};
  background: ${({ $flat, theme }) => ($flat ? 'transparent' : theme.colors.gray050)};
  display: grid;
  gap: 0.75rem;
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: baseline;
`;

const LineContent = styled.div`
  display: grid;
  gap: 0.1rem;
  min-width: 0;
`;

const LineContentButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.1rem;
  min-width: 0;
  text-align: left;
  cursor: pointer;
`;

const Title = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray900};
  font-weight: 600;
`;

const Meta = styled.p`
  margin: 0.2rem 0 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.86rem;
`;

const Amount = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray900};
  font-weight: 700;
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
  margin: 0.2rem 0;
`;

const Accent = styled(Amount)`
  color: ${({ theme }) => theme.colors.primary};
`;

const RemoveButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.8rem;
  cursor: pointer;
`;

type OrderBreakdownProps = {
  readonly order: CalculatedOrder;
  readonly onRemoveSku?: (skuId: string) => void;
  readonly onSelectLineItem?: (item: CalculatedOrderLineItem) => void;
  readonly hidePrices?: boolean;
  readonly flat?: boolean;
  readonly dashedItems?: boolean;
};

export default function OrderBreakdown({
  order,
  onRemoveSku,
  onSelectLineItem,
  hidePrices = false,
  flat = false,
  dashedItems = false,
}: OrderBreakdownProps) {
  return (
    <Wrap $flat={flat}>
      {order.lineItems.map((item) => (
        <Line
          key={item.skuId}
          style={
            dashedItems
              ? {
                  border: '1px dashed rgba(255, 255, 255, 0.24)',
                  borderRadius: '14px',
                  padding: '0.82rem 0.88rem',
                  background: 'transparent',
                  alignItems: 'flex-start',
                }
              : undefined
          }
        >
          {onSelectLineItem ? (
            <LineContentButton
              type="button"
              onClick={() => onSelectLineItem(item)}
              aria-label={`Open ${item.name} snapshot`}
            >
              <Title>{item.name}</Title>
              <Meta>
                {item.billingType === 'recurring' ? 'Monthly' : 'One-time'} x{item.quantity}
              </Meta>
              {onRemoveSku ? (
                <Meta>
                  <RemoveButton
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveSku(item.skuId);
                    }}
                  >
                    Remove
                  </RemoveButton>
                </Meta>
              ) : null}
            </LineContentButton>
          ) : (
            <LineContent>
              <Title>{item.name}</Title>
              <Meta>
                {item.billingType === 'recurring' ? 'Monthly' : 'One-time'} x{item.quantity}
              </Meta>
              {onRemoveSku ? (
                <Meta>
                  <RemoveButton type="button" onClick={() => onRemoveSku(item.skuId)}>
                    Remove
                  </RemoveButton>
                </Meta>
              ) : null}
            </LineContent>
          )}
          {hidePrices ? null : <Amount>{formatUsdCents(item.lineSubtotalCents)}</Amount>}
        </Line>
      ))}

      {!hidePrices ? (
        <>
          <Divider />
          <Line>
            <Title>Recurring subtotal</Title>
            <Amount>{formatUsdCents(order.subtotalRecurringCents)}</Amount>
          </Line>
          <Line>
            <Title>One-time subtotal</Title>
            <Amount>{formatUsdCents(order.subtotalOneTimeCents)}</Amount>
          </Line>
          {order.discountCents > 0 ? (
            <Line>
              <Title>{order.discountReason ?? 'Discount'}</Title>
              <Accent>-{formatUsdCents(order.discountCents)}</Accent>
            </Line>
          ) : null}
          <Divider />
          <Line>
            <Title>Due today</Title>
            <Accent>{formatUsdCents(order.totalDueNowCents)}</Accent>
          </Line>
          <Line>
            <Title>Ongoing monthly</Title>
            <Accent>{formatUsdCents(order.recurringMonthlyCents)}</Accent>
          </Line>
        </>
      ) : null}
    </Wrap>
  );
}
